import React from "react";
import { Checkbox } from "../../../components/ui";
import { EXTERNAL_LINKS } from "../../../routes/paths";
import type { ConsentDocumentType } from "../types/consent.types";

/**
 * The DPDP terms themselves, and the ticks that agree to them.
 *
 * Shared by the two screens that ask: the sign-up consent screen, which asks
 * before an account exists, and the gate a returning user meets when a document
 * has been published or republished since they last agreed. They have to state
 * the terms in exactly the same words - it is the same agreement - so the rows
 * live here rather than being written out twice and drifting apart.
 */

/** One tick, in the order the rows are shown. */
export type ConsentRowKey = "terms" | "dpa" | "age";

/**
 * A document named inside a row's words.
 *
 * It carries where it is named rather than a piece of markup, because the two
 * widths put it in different places: a desktop draws the link through the
 * sentence where the phrase sits, and a phone lifts it out of the sentence into
 * a chip under the row. Spelling it as a phrase and a destination is what lets
 * both be built from the one copy below - see `ConsentRow.label`.
 */
export interface ConsentLink {
  /** The exact phrase in the row's `label` or `description` that names it. */
  phrase: string;
  href: string;
  /** Which of the row's two strings the phrase is in. */
  in: "label" | "description";
  /**
   * The phrase is there to be pressed and nothing else, so a phone drops it
   * from the sentence rather than leaving it sitting there unlinked.
   *
   * "Read DPA" is the case: with the chip carrying it, a trailing "Read DPA"
   * in the description would be an instruction to press something that is no
   * longer there. A phrase like "Terms of use" is the opposite - it is part of
   * what is being agreed to, so it stays in the sentence at either width.
   */
  standalone?: boolean;
  /** What the phone's chip calls it, where the phrase itself does not read as
   *  the name of a document. */
  chipLabel?: string;
}

export interface ConsentRow {
  key: ConsentRowKey;
  /**
   * The backend documents this row stands for.
   *
   * The first row covers two. Agreeing to the Terms of use and the Privacy
   * policy is one decision as far as the user is concerned, and four separate
   * boxes would be a worse screen for no more consent.
   */
  documents: readonly ConsentDocumentType[];
  /**
   * The row's sentence, as one string.
   *
   * A string rather than the node it used to be, because the sentence is the
   * agreement and the two widths now set it differently - the links drawn
   * through it on a desktop, lifted out of it on a phone. Written as markup it
   * would have had to be written twice, and two copies of the words somebody
   * is consenting to is the one thing this file exists to prevent.
   */
  label: string;
  description?: string;
  links?: readonly ConsentLink[];
}

/** One size and colour for every row, over the form default the shared
 *  checkbox uses everywhere else. */
const ROW_LABEL = "text-[15px] 3xl:text-[15px] text-[#1E1F24]";

const ExternalLink: React.FC<{ href: string; children: React.ReactNode }> = ({
  href,
  children,
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="text-[#00C2CB] font-medium hover:underline"
  >
    {children}
  </a>
);

export const CONSENT_ROWS: readonly ConsentRow[] = [
  {
    key: "terms",
    documents: ["TERMS_OF_USE", "PRIVACY_POLICY"],
    label: "I agree to the Terms of use and Privacy policy.",
    links: [
      { phrase: "Terms of use", href: EXTERNAL_LINKS.termsOfUse, in: "label" },
      {
        phrase: "Privacy policy",
        href: EXTERNAL_LINKS.privacyPolicy,
        in: "label",
      },
    ],
  },
  {
    key: "dpa",
    documents: ["DATA_PROCESSING_AGREEMENT"],
    label: "I agree to the Data Processing Agreement.",
    description:
      "I’ll make sure my clients are happy to share any health data I handle. Read DPA",
    links: [
      {
        phrase: "Read DPA",
        href: EXTERNAL_LINKS.dataProcessingAgreement,
        in: "description",
        standalone: true,
      },
    ],
  },
  {
    key: "age",
    documents: ["AGE_ATTESTATION"],
    label: "I confirm I’m 18 years of age or older.",
    description: "Naurist is for practitioners aged 18 and above.",
  },
];

/**
 * The rows covering at least one of the given documents, in display order.
 *
 * What the backend says is outstanding is what gets asked about: a user who is
 * only being shown a republished Privacy policy should not be made to confirm
 * their age a second time to get past it.
 */
export const consentRowsFor = (
  types: readonly ConsentDocumentType[]
): ConsentRow[] =>
  CONSENT_ROWS.filter((row) =>
    row.documents.some((document) => types.includes(document))
  );

/** Every document type the given rows stand for. */
export const documentsOf = (
  rows: readonly ConsentRow[]
): ConsentDocumentType[] => rows.flatMap((row) => [...row.documents]);

/**
 * The sentence with its links drawn through it, which is what a desktop reads.
 *
 * Each phrase is taken in turn out of what is left of the sentence, so the
 * walk cannot double back and a phrase that appears twice links the first of
 * them.
 */
const withLinks = (text: string, links: readonly ConsentLink[]) => {
  const parts: React.ReactNode[] = [];
  let rest = text;

  links.forEach((link, index) => {
    const at = rest.indexOf(link.phrase);
    if (at < 0) return;
    if (at > 0) parts.push(rest.slice(0, at));
    parts.push(
      <ExternalLink key={index} href={link.href}>
        {link.phrase}
      </ExternalLink>
    );
    rest = rest.slice(at + link.phrase.length);
  });

  if (rest) parts.push(rest);
  return parts;
};

/** The same sentence with nothing drawn through it, which is what a phone
 *  reads - its documents are the chips under the row instead. */
const withoutLinks = (text: string, links: readonly ConsentLink[]) =>
  links
    .filter((link) => link.standalone)
    .reduce((carried, link) => carried.replace(link.phrase, ""), text)
    .replace(/\s+/g, " ")
    .trim();

/** Both settings of one of the row's strings, the width picking between them.
 *  One of the two is `display: none` at any given width, so the control's
 *  accessible name is the sentence once. */
const Words: React.FC<{ text: string; links: readonly ConsentLink[] }> = ({
  text,
  links,
}) =>
  links.length === 0 ? (
    <>{text}</>
  ) : (
    <>
      <span className="sm:hidden">{withoutLinks(text, links)}</span>
      <span className="hidden sm:inline">{withLinks(text, links)}</span>
    </>
  );

/**
 * On a phone the terms are one grouped sheet of ruled rows; from sm up they are
 * the ruled list on the open page they have always been.
 *
 * The sheet is what a phone app puts a short list of settings in, and that is
 * what this is: a handful of rows that belong together, held in one surface
 * with a rule between each, rather than three cards floating separately down
 * the glass. Carding them said there are three decisions here, which was the
 * right thing to say and the wrong way to say it - three borders and three
 * shadows is a lot of furniture for three sentences, and the eye reads the
 * boxes before it reads the words. One sheet says the same thing with one
 * border, and a rule between two rows is enough once they share an edge.
 *
 * A ticked row tints to the brand's palest teal across the full width of the
 * sheet. That is the row itself answering rather than a card lighting up, and
 * it is still what lets somebody see how far through the screen they are
 * without going back over the words.
 *
 * The documents come out of the sentences and sit under them as chips. A link
 * inside a label is a target inside a target: the row is the thing to press to
 * agree, and on a phone the two are a thumb's width apart. Lifting them out
 * gives the sentence back to the tick and the document a button of its own.
 * From sm up they go back inline, where a cursor can tell a 90px link from the
 * row around it.
 */
export const ConsentTerms: React.FC<{
  rows: readonly ConsentRow[];
  agreed: Readonly<Partial<Record<ConsentRowKey, boolean>>>;
  onToggle: (key: ConsentRowKey, checked: boolean) => void;
}> = ({ rows, agreed, onToggle }) => (
  <div
    className={
      "mt-4 overflow-hidden rounded-[8px] border border-[#E9EDF1] bg-white " +
      "shadow-[0_1px_2px_rgba(30,31,36,0.04)] divide-y divide-[#EDF0F2] " +
      "sm:rounded-none sm:border-x-0 sm:border-b-0 sm:border-t " +
      "sm:border-[#EDF0F2] sm:bg-transparent sm:shadow-none"
    }
  >
    {rows.map((row) => {
      const checked = !!agreed[row.key];
      const links = row.links ?? [];
      const labelLinks = links.filter((link) => link.in === "label");
      const descriptionLinks = links.filter(
        (link) => link.in === "description"
      );

      return (
        <div
          key={row.key}
          className={`px-4 py-4 transition-colors sm:px-0 ${
            checked ? "bg-[#F2FCFD] sm:bg-transparent" : ""
          }`}
        >
          {/* Beside the first line, not the middle of the block. Every one of
              these labels is a sentence, and on a phone every one of them
              wraps - a centred box then sits in the gap between the two lines
              it has made, reading as punctuation between them rather than as
              the tick for the sentence that starts above it. */}
          <Checkbox
            checked={checked}
            onCheckedChange={(next) => onToggle(row.key, next)}
            labelClassName={ROW_LABEL}
            size="touch"
            align="first-line"
            label={<Words text={row.label} links={labelLinks} />}
            description={
              row.description ? (
                <Words text={row.description} links={descriptionLinks} />
              ) : undefined
            }
          />

          {/* Outside the label on purpose, so that pressing one opens a
              document rather than agreeing to it, and indented to the label's
              own left edge - the 20px box and the 8px beside it. */}
          {links.length > 0 && (
            <div className="mt-2.5 flex flex-wrap gap-2 pl-7 sm:hidden">
              {links.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-[34px] items-center rounded-full border border-[#CCF0F3] bg-white px-3 text-[13px] font-medium text-[#00A3AB] active:bg-[#E6FAFB]"
                >
                  {link.chipLabel ?? link.phrase}
                </a>
              ))}
            </div>
          )}
        </div>
      );
    })}
  </div>
);
